import { translateUi } from "@/shared/lib/ui-translate";
import { stripLocale } from "@/i18n/routing";
import { api as coreApi } from "@/lib/axiosClient";
import { AxiosError } from "axios";
import type { AxiosRequestConfig, Method } from "axios";
import {
  isOperationResultFailure,
  extractOperationFailureMessage,
  OPERATION_FAILED_CODE,
} from "@/shared/api/operation-result";
import { handleSilentError } from "@/shared/api/error-handler";
import { createMockAxiosAdapter } from "@/shared/lib/mock-api";
import { normalizeKeysToCamelCase } from "@/shared/lib/normalize-keys";
import { useAuthStore } from "@/shared/model/auth-store";
import { getQueryClient } from "@/shared/query-client";

const api = coreApi;

const isPlainRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null;

const authQueryPrefixes = ["/me", "/auth", "/oauth"] as const;

const isAuthQueryKey = (queryKey: unknown) => {
  if (!Array.isArray(queryKey)) return false;
  return queryKey.some((entry) => {
    if (typeof entry !== "object" || entry === null || !("url" in entry)) {
      return false;
    }
    const url = (entry as { url?: unknown }).url;
    if (typeof url !== "string") return false;
    return authQueryPrefixes.some((prefix) => url.startsWith(prefix));
  });
};

const clearAuthQueries = () => {
  const queryClient = getQueryClient();
  queryClient.removeQueries({
    predicate: (query) => isAuthQueryKey(query.queryKey),
  });
};

const extractBanInfo = (
  data: unknown,
): { reason: string | null; bannedAt: string | null } | null => {
  if (!isPlainRecord(data)) return null;

  if (data["code"] !== "USER_BANNED") return null;

  const reasonText = typeof data["reason"] === "string" ? data["reason"] : null;
  const bannedAt =
    typeof data["bannedAt"] === "string" ? data["bannedAt"] : null;

  return {
    reason:
      reasonText ||
      (typeof data["message"] === "string" ? data["message"] : null),
    bannedAt,
  };
};

// Типы для Kubb-сгенерированного клиента
// KAN-208: data — TVariables либо FormData: сген. Kubb-хуки для upload-ручек
// (логотипы/скриншоты/реплеи) шлют multipart FormData вместо DTO.
export interface RequestConfig<TVariables = unknown>
  extends AxiosRequestConfig<TVariables | FormData> {
  signal?: AbortSignal;
  data?: TVariables | FormData;
}

export interface ResponseConfig<T = unknown> {
  data: T;
  status: number;
  statusText: string;
  headers: Record<string, string>;
}

/**
 * @public Импортируется только сгенерированными Kubb-хуками: их каталог
 * исключён из анализа knip, поэтому в его списке тип выглядит мёртвым.
 */
export type ResponseErrorConfig<T = unknown> = AxiosError<T>;

// Функция fetch для Kubb (обертка над Axios)
async function fetch<
  TData = unknown,
  _TError = unknown,
  TVariables = unknown,
>(
  config: RequestConfig<TVariables> & {
    method?: string;
    url: string;
    data?: TVariables | FormData;
  },
): Promise<ResponseConfig<TData>> {
  const { method = "GET", url, data, ...axiosConfig } = config;

  if (process.env["MCP_API_BASE_URL"] && axiosConfig.baseURL) {
    axiosConfig.baseURL = process.env["MCP_API_BASE_URL"];
  }

  const response = await api.request<TData>({
    ...axiosConfig,
    method: method.toUpperCase() as Method,
    url,
    data,
  });

  return {
    data: response.data,
    status: response.status,
    statusText: response.statusText,
    headers: response.headers as Record<string, string>,
  };
}

const USE_MOCKS =
  String(process.env.NEXT_PUBLIC_USE_MOCKS ?? "").toLowerCase() === "true";

if (USE_MOCKS) {
  api.defaults.adapter = createMockAxiosAdapter();
  console.warn(
    "[API] Using mock adapter. Disable via NEXT_PUBLIC_USE_MOCKS=false",
  );
}

api.interceptors.response.use(
  (response) => {
    // KAN-1180: 204 No Content (так уезжает Ok(null) в ASP.NET) axios отдаёт
    // пустой строкой. Приводим к null здесь, чтобы `data == null` в queryFn
    // работало везде, а "" не попадало в кэш вне своего типа.
    if (response.status === 204 || response.data === "") {
      response.data = null;
    }
    // Автоматическая нормализация PascalCase → camelCase для всех ответов
    if (response.data && typeof response.data === "object") {
      response.data = normalizeKeysToCamelCase(
        response.data as Record<string, unknown>,
      );
    }
    // KAN-311: 200 + OperationResultDto{success:false} — провал операции,
    // невидимый для error-пайплайна. Превращаем в reject, чтобы call-site
    // не показал ложный успех. Только мутации: у GET success может быть
    // легитимной семантикой состояния.
    const respMethod = String(response.config?.method ?? "get").toLowerCase();
    if (respMethod !== "get" && isOperationResultFailure(response.data)) {
      const failMessage =
        extractOperationFailureMessage(response.data) ?? translateUi("litApi.operatsiyaNeVypolnena");
      const syntheticData = { code: OPERATION_FAILED_CODE, message: failMessage };
      return Promise.reject(
        new AxiosError(
          failMessage,
          OPERATION_FAILED_CODE,
          response.config,
          response.request,
          { ...response, data: syntheticData },
        ),
      );
    }
    return response;
  },
  (error) => {
    const status = error.response?.status as number | undefined;
    const message = String(
      error.response?.data?.message || error.response?.data?.error || "",
    );
    const url = String(error.config?.url || "");
    const method = String(error.config?.method || "").toLowerCase();
    
    // Обработка 403 (бан пользователя) — только если это именно бан
    if (status === 403 && typeof window !== "undefined") {
      const banInfo = extractBanInfo(error.response?.data);
      if (banInfo) {
        clearAuthQueries();
        useAuthStore.getState().setIsAuthenticated(false);

        // Сохраняем информацию о бане в sessionStorage
        try {
          sessionStorage.setItem("banInfo", JSON.stringify(banInfo));
        } catch {
          // Игнорируем ошибки sessionStorage
        }

        // Редирект на страницу бана
        if (window.location.pathname !== "/banned") {
          const params = new URLSearchParams();
          if (banInfo.reason) params.set("reason", banInfo.reason);
          if (banInfo.bannedAt) params.set("bannedAt", banInfo.bannedAt);
          const queryString = params.toString();
          window.location.href = `/banned${queryString ? `?${queryString}` : ""}`;
        }
      }

      return Promise.reject(error);
    }
    
    if (!error.response) {
      const isCanceled =
        error.code === "ERR_CANCELED" ||
        error.name === "CanceledError" ||
        String(error.message || "").toLowerCase().includes("canceled");
      if (isCanceled) {
        return Promise.reject(error);
      }

      const isNetworkError = error.code === "ECONNABORTED" || error.message?.includes("timeout") || error.message?.includes("Network Error");
      // Обрыв сети сессию не отменяет: раньше здесь любой сбой /me* (включая
      // /me/desktop-token) разлогинивал, и без сети пользователя гоняло по кругу
      // /profile ↔ /login (KAN-1282). Выход — только по ответу сервера 401/403 ниже;
      // снятие «загрузки» при недоступном бэкенде делает initializeAuth.
      if (isNetworkError) {
        handleSilentError(error, {
          component: "api",
          action: "responseInterceptor",
          status: 0,
          url,
          method,
          errorType: "network",
        });
      }
      return Promise.reject(error);
    }
    
    // Обработка 401 (сессия истекла или была отозвана)
    if (status === 401 && typeof window !== "undefined") {
      const wasAuthenticated = useAuthStore.getState().isAuthenticated;

      clearAuthQueries();

      // Сбрасываем состояние аутентификации
      useAuthStore.getState().setIsAuthenticated(false);
      useAuthStore.getState().logout();

      // Редирект только если пользователь БЫЛ аутентифицирован (сессия истекла),
      // и это не ожидаемый 401 (проверка /me). Анонимные пользователи на публичных
      // страницах (/u/*, /tournaments/* и т.д.) не должны редиректиться.
      // Штатные GET-401: /me (проверка сессии) и /tester/status (проба «ты
      // тестер?» — для анонима/не-тестера 401 нормален, не баг и не повод
      // редиректить).
      const isExpected401 =
        method === "get" &&
        (/\/(me|tester\/status)\b/i.test(url) ||
          /\/organizations\/user\//i.test(url));
      if (wasAuthenticated && !isExpected401 && !["/login", "/", "/banned"].includes(stripLocale(window.location.pathname))) {
        window.location.href = "/";
      }
    }
    
    // 502/503/504 — gateway/upstream временно недоступен. Чаще всего это
    // момент деплоя backend (Traefik не успел переключиться на новый pod)
    // или backend restart. Юзер сам через 2-5 секунд получит свежий ответ
    // на retry. Логировать как «настоящий» баг бессмысленно — шумит
    // crash-reports. Полностью глушим до user'а (никакого toast/redirect).
    const isTransientGateway = status === 502 || status === 503 || status === 504;

    // Бизнес-валидации с понятным сообщением — не системные баги, обработка
    // на месте через try/catch + toast. См. ai/known-issues.md → задача про
    // crash-log noise от "Турнир уже анонсирован" etc.
    const businessMessages = ["уже анонсирован", "уже опубликован", "уже зарегистрирован", "уже закрыт", "already exists", "already announced"];
    const isBusinessValidation = status === 400
      && businessMessages.some(m => message.toLowerCase().includes(m));

    const isExpected =
      status === 404 ||
      isTransientGateway ||
      isBusinessValidation ||
      (status === 401 &&
        method === "get" &&
        (/\/(me|tester\/status)\b/i.test(url) ||
          /\/organizations\/user\//i.test(url))) ||
      (status === 400 &&
        message.toLowerCase().includes("request already sent"));

    // Не логируем 404 / 5xx-transient / business 400 как системные ошибки —
    // они часть нормального flow и шумят в crash-reports.
    if (!isExpected) {
      handleSilentError(error, {
        component: "api",
        action: "responseInterceptor",
        status,
        url,
        method,
      });
    }
    return Promise.reject(error);
  },
);

/** @public Тип клиента для сгенерированных хуков — см. комментарий выше. */
export type Client = typeof fetch;
// Наружу отдаём только дефолт: именно его импортируют сгенерированные Kubb-хуки
// (`import fetch from "@/shared/lib/api"`). Второго имени у той же функции быть не должно.
export default fetch;
export { api };
